import {
  Box,
  Text,
  TextLink,
  Heading,
  Stack,
  Tiles,
  Link,
} from 'braid-design-system';
import React from 'react';

import { ArticleCard } from './ArticleCard';
import { type SiteName, createUrlResolver } from '@seek/melways-sites';
import { useCMSUContext } from '../../hooks/useCMSUContext';
import type { Locale } from '@seek/cmsu-cms-connect';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';

export type LatestArticles = Array<{
  __typename?: 'Article';
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  publishDate: any;
  featureImage: Array<{
    __typename?: 'Asset';
    id: string;
    fileName: string;
    locale: Locale;
    url: string;
  }>;
  category?: {
    __typename?: 'Category';
    name: string;
    slug: string;
    parent?: {
      __typename?: 'Category';
      id: string;
      name: string;
    } | null;
  } | null;
}>;

type Props = {
  categoryName: string;
  categorySlug: string;
  latestArticles: LatestArticles | undefined;
  section: string;
  site: SiteName;
};

export const LatestArticlesByCategory = ({
  categoryName,
  categorySlug,
  section,
  latestArticles,
  site,
}: Props) => {
  const { language } = useCMSUContext();
  const { t } = useTranslations(translations);

  const urlResolver = createUrlResolver({ site, language });

  return (
    <Stack space="xlarge">
      <Box display="flex" justifyContent="spaceBetween">
        <Heading level="4">{categoryName}</Heading>
        <Box display="flex">
          <Text>
            <TextLink
              href={urlResolver({
                path: `/${section}/category/${categorySlug}`,
              })}
              hitArea="large"
            >
              {t('More')}
            </TextLink>
          </Text>
        </Box>
      </Box>
      <Tiles
        space="gutter"
        columns={{
          mobile: 1,
          tablet: 2,
          desktop: 3,
        }}
      >
        {latestArticles &&
          latestArticles.map((article) => (
            <Link
              href={urlResolver({
                path: `/${section}/article/${article.slug}`,
              })}
              key={article.id}
            >
              <ArticleCard article={article} />
            </Link>
          ))}
      </Tiles>
    </Stack>
  );
};
